import React, { useState, useMemo, useContext } from 'react';

import { Card, PrimaryButton, IOTTableWrapper, useSnackbar, DeleteDialog } from '@Iot-Bee/standard-web-library';

import AlertDialog from './components/AlertDialog';
import { noCacheFetcher } from '../../utils/fetch';
import { Alert, ServerAlert } from '../../Types/types';
import { DevicesContext } from '../../context/DevicesContext';
import { useCustomSWR } from '../../hooks/customSWR';
import { alertToDisplay, columns, validateCondition } from './util';

export function AlertsPage() {
  const devicesContext = useContext(DevicesContext);

  if (!devicesContext) {
    throw new Error('DevicesContext is not defined');
  }
  const { devices } = devicesContext;

  const {
    data: rawAlerts,
    isLoading: alertsLoading,
    mutate: refreshAlerts,
  } = useCustomSWR<ServerAlert[]>(`${process.env.REACT_APP_API_URL}getalerts`);

  const displayAlerts = useMemo(() => {
    if (alertsLoading) return [];
    if (!rawAlerts) return [];

    return alertToDisplay(rawAlerts, devices);
  }, [rawAlerts, devices]);

  const [dialogInfo, setDialogInfo] = useState<{
    open: boolean;
    mode: 'add' | 'edit';
    alertToEdit: Alert | null;
  }>({
    open: false,
    mode: 'add',
    alertToEdit: null,
  });
  const [conditionError, setConditionError] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    alert: Alert;
  }>({ open: false, alert: {} as Alert });

  const {
    actions: { openSnackbar },
  } = useSnackbar();

  const handleCreateAlert = (alertData: Alert) => {
    const conditionIsValid = validateCondition(alertData.condition);
    if (conditionIsValid !== true) {
      openSnackbar('error', 'The condition is not valid');
      setConditionError(conditionIsValid);
      return;
    }
    const data = {
      ...alertData,
      deviceIds: alertData.deviceIds.join(', '),
      sensorType: alertData.sensorType.join(', '),
      customHeaders: JSON.stringify(alertData.customheaders),
      mqttCredentials: JSON.stringify(alertData.mqttCredentials),
    };

    noCacheFetcher(`${process.env.REACT_APP_API_URL}addalert`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    })
      .then(() => {
        const friendlyNames: string[] = [];
        //convert from friendly names to device ids
        alertData.deviceIds.forEach((element) => {
          const device = devices.find((elm) => elm.id === element);
          if (device) {
            if (device.deviceName) {
              friendlyNames.push(device.deviceName);
            } else {
              friendlyNames.push(device.id);
            }
          }
        });
        alertData.deviceIds = friendlyNames;
        refreshAlerts();
        setDialogInfo({ open: false, mode: 'add', alertToEdit: null });
        setConditionError('');
        openSnackbar('success', 'Alert created successfully');
      })
      .catch((error) => {
        openSnackbar('error', 'Error creating alert');
        console.error(error);
      });
  };

  const handleDeleteAlert = (alertData: Alert) => {
    const data = {
      id: alertData.id,
    };

    noCacheFetcher(`${process.env.REACT_APP_API_URL}deletealert`, {
      method: 'DELETE',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    })
      .then(() => {
        openSnackbar('success', 'Alert deleted successfully');
        refreshAlerts();
        setDeleteDialog((state) => {
          return { ...state, open: false };
        });
      })
      .catch((error) => {
        openSnackbar('error', 'Error deleting alert');
        console.error(error);
      });
  };

  const handleEditAlert = (alertData: Alert) => {
    const conditionIsValid = validateCondition(alertData.condition);
    if (!conditionIsValid) {
      openSnackbar('error', 'The condition is not valid');
      return;
    }
    const data = {
      ...alertData,
      sensorType: alertData.sensorType.join(', '),
      deviceIds: alertData.deviceIds.join(', '),
      customHeaders: JSON.stringify(alertData.customheaders),
      mqttCredentials: JSON.stringify(alertData.mqttCredentials),
    };

    noCacheFetcher(`${process.env.REACT_APP_API_URL}editalert`, {
      method: 'PUT',
      headers: new Headers({
        // Your header content
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    })
      .then(() => {
        refreshAlerts();
        setDialogInfo({ open: false, mode: 'add', alertToEdit: null });
        setConditionError('');
        openSnackbar('success', 'Alert edited successfully');
      })
      .catch(() => {
        openSnackbar('error', 'Error editing alert');
        console.error('error');
      });
  };

  return (
    <Card
      title={'Alerts'}
      headerActions={
        <PrimaryButton
          onClick={() => {
            setDialogInfo({ open: true, mode: 'add', alertToEdit: null });
          }}
        >
          Add new alert
        </PrimaryButton>
      }
    >
      <IOTTableWrapper
        columns={columns}
        data={displayAlerts}
        onEdit={(alertItem) => {
          setDialogInfo({ open: true, mode: 'edit', alertToEdit: alertItem });
        }}
        onDelete={(alertItem) => {
          setDeleteDialog({ open: true, alert: alertItem });
        }}
        isLoading={alertsLoading}
      />
      <AlertDialog
        alertItem={dialogInfo.alertToEdit}
        onClose={() => setDialogInfo({ open: false, mode: 'add', alertToEdit: null })}
        onSave={dialogInfo.mode === 'add' ? handleCreateAlert : handleEditAlert}
        conditionValidator={validateCondition}
        conditionError={conditionError}
        clientIdDevices={devices}
        open={dialogInfo.open}
      />
      <DeleteDialog
        title={deleteDialog.alert?.name}
        open={deleteDialog.open}
        handleDelete={() => {
          handleDeleteAlert(deleteDialog.alert);
        }}
        onClose={() =>
          setDeleteDialog((state) => {
            return { ...state, open: false };
          })
        }
        itemName={deleteDialog.alert?.name}
      />
    </Card>
  );
}

export default AlertsPage;
